import {
  AfterViewInit,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

export interface HighlightRule {
  pattern: string | RegExp;
  className: string;
  caseSensitive?: boolean;
  group? : number;
}

@Directive({
  selector: '[textHighlight]',
  standalone: true
})
export class TextHighlightDirective implements OnInit, AfterViewInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  textHighlight = input<HighlightRule[]>([]);
  
  private inputElement: HTMLInputElement;
  private highlights: HTMLDivElement;
  
  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {
    this.inputElement = this.el.nativeElement;
    
    effect(() => {
      this.updateHighlights();
    });
  }

  ngOnInit() {
    this.createBackdropStructure();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setupEventListeners();
      this.updateHighlights();
      this.recalculateAlignment();
    }, 100);
  }

  private recalculateAlignment() {
    if (!this.inputElement || !this.highlights) return;
    
    this.copyStylesToOverlay();
  }

  ngOnDestroy() {
    if (this.highlights) {
      this.highlights.remove();
    }
  }

  private createBackdropStructure() {
    const parent = this.inputElement.parentElement;
    if (!parent) return;

    this.highlights = this.renderer.createElement('div');
    
    this.renderer.setStyle(this.highlights, 'position', 'absolute');
    this.renderer.setStyle(this.highlights, 'pointerEvents', 'none');
    this.renderer.setStyle(this.highlights, 'zIndex', '1');
    this.renderer.setStyle(this.highlights, 'color', 'transparent');
    this.renderer.setStyle(this.highlights, 'overflow', 'hidden');
    this.renderer.setStyle(this.highlights, 'color', 'black');
    
    this.renderer.setStyle(this.inputElement, 'position', 'relative');
    this.renderer.setStyle(this.inputElement, 'zIndex', '2');
    this.renderer.setStyle(this.inputElement, 'background', 'transparent');
    this.renderer.setStyle(this.inputElement, 'backgroundColor', 'transparent');
    
    this.copyStylesToOverlay();
    
    this.renderer.insertBefore(parent, this.highlights, this.inputElement);
  }

  private copyStylesToOverlay() {
    if (!this.inputElement || !this.highlights) return;
    
    const inputStyles = window.getComputedStyle(this.inputElement);
    const inputRect = this.inputElement.getBoundingClientRect();
    const parentRect = this.inputElement.parentElement!.getBoundingClientRect();
    
    this.renderer.setStyle(this.highlights, 'top', (inputRect.top - parentRect.top) + 'px');
    this.renderer.setStyle(this.highlights, 'left', (inputRect.left - parentRect.left) + 'px');
    this.renderer.setStyle(this.highlights, 'width', inputRect.width + 'px');
    this.renderer.setStyle(this.highlights, 'height', inputRect.height + 'px');
    
    const stylesToCopy = [
    'fontSize', 'fontFamily', 'fontWeight', 'lineHeight', 'letterSpacing',
    'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'textAlign', 'textIndent', 'border', 'borderRadius', 'boxSizing',
    'whiteSpace', 'wordBreak', 'overflowWrap' 
  ];
    
    stylesToCopy.forEach(style => {
      const value = inputStyles.getPropertyValue(style);
      if (value) {
        this.renderer.setStyle(this.highlights, style, value);
      }
    });
  }

  private setupEventListeners() {
    fromEvent(this.inputElement, 'input')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        setTimeout(() => {
          this.updateHighlights();
        }, 0);
      });

    fromEvent(this.inputElement, 'change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateHighlights();
      });

    fromEvent(this.inputElement, 'keyup')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateHighlights();
      });

    fromEvent(this.inputElement, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.handleScroll();
      });
  }

  private handleScroll() {
    if (this.highlights && this.inputElement) {
      this.highlights.scrollTop = this.inputElement.scrollTop;
      this.highlights.scrollLeft = this.inputElement.scrollLeft;
    }
  }

  private updateHighlights() {
    if (!this.highlights) return;
    
    const text = this.inputElement.value || '';
    
    if (!text.trim()) {
      this.renderer.setProperty(this.highlights, 'innerHTML', '');
      return;
    }
    
    let highlightedText = text;
    
    if (this.textHighlight().length > 0) {
      highlightedText = this.applyHighlights(text);
    }
    
    this.renderer.setProperty(this.highlights, 'innerHTML', highlightedText);
  }

  public clearHighlights(): void {
    if (this.highlights) {
      this.renderer.setProperty(this.highlights, 'innerHTML', '');
    }
  }

  private applyHighlights(text: string): string {
    if (!text || !this.textHighlight().length) {
      return text;
    }

    let result = text;

    this.textHighlight().forEach(rule => {
      result = this.applyHighlightRule(result, rule);
    });

    return result;
  }

  private applyHighlightRule(text: string, rule: HighlightRule): string {
    let regex: RegExp;

    if (rule.pattern instanceof RegExp) {
      regex = rule.pattern;
    } else {
      const flags = rule.caseSensitive === false ? 'gi' : 'g';
      const escapedPattern = this.escapeRegExp(rule.pattern);

      const hasSpecialChars = /[()[\]{}+\-*/%=<>!&|^~]/.test(rule.pattern);
      const hasSpaces = rule.pattern.includes(' ');

      if (hasSpecialChars || hasSpaces) {
        regex = new RegExp(`(${escapedPattern})`, flags);
      } else {
        regex = new RegExp(`\\b(${escapedPattern})\\b`, flags);
      }
    }

    return text.replace(regex, (...args) => {
      const match = args[0];
      const groups = args.slice(1, -2);
      const groupIndex = rule.group ?? 1;
      if (groups[groupIndex - 1] !== undefined) {
        const before = match.slice(0, match.indexOf(groups[groupIndex - 1]));
        const after = match.slice(match.indexOf(groups[groupIndex - 1]) + groups[groupIndex - 1].length);
        return `${before}<span class="${rule.className}">${groups[groupIndex - 1]}</span>${after}`;
      }
      return `<span class="${rule.className}">${match}</span>`;
    });
  }

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
